/**
 * Jobs API Module
 * Handles all job-related API operations
 */

import { buildApiUrl, CURRENT_API_VERSION } from './config';

/**
 * Sign up a new user and create a job request
 * @param {Object} userData - User information
 * @param {string} userData.email - User's email address
 * @param {string|null} userData.firstName - User's first name (optional)
 * @param {string|null} userData.lastName - User's last name (optional)
 * @param {string|null} userData.phoneNumber - User's phone number (optional)
 * @param {string} userData.address - User's property address
 * @param {Object} jobRequest - Job request details
 * @param {Object} jobRequest.location - Property location
 * @param {number} jobRequest.location.lat - Latitude
 * @param {number} jobRequest.location.lon - Longitude
 * @param {string} jobRequest.location.name - Location name
 * @param {string} jobRequest.jobKind - Type of job (e.g., "Driveway")
 * @param {number} [version] - API version to use (defaults to current version)
 * @returns {Promise<Object>} Response data from the API
 * @throws {Error} If the request fails
 */
export async function signUpAndAddJob(userData, jobRequest, version = CURRENT_API_VERSION) {
  const url = buildApiUrl('/Public/Jobs/SignUpAndAddJob', version);
  
  const payload = {
    userData,
    jobRequest,
  };

  // console.log('[API/Jobs] Submitting job request:', payload);

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    // const errorBody = await response.text();
    // console.error(
    //   `[API/Jobs] Request failed with status ${response.status}:`,
    //   errorBody
    // );
    throw new Error(
      `Request failed with status ${response.status}`
    );
  }

  const data = await response.json();
  // console.log('[API/Jobs] Job created successfully:', data);

  return data;
}

/**
 * Verify a job request using a request ID
 * @param {string} requestId - The unique request ID from the verification email
 * @param {number} [version] - API version to use (defaults to current version)
 * @returns {Promise<Object>} Job verification data including status, location, and quote
 * @throws {Error} If the verification fails
 */
export async function verifyJob(requestId, version = CURRENT_API_VERSION) {
  const url = buildApiUrl(`/Public/Jobs/VerifyJob?requestId=${encodeURIComponent(requestId)}`, version);

  // console.log('[API/Jobs] Verifying job url:', url);

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    // const errorBody = await response.text();
    // console.error(
    //   `[API/Jobs] Verification failed with status ${response.status}:`,
    //   errorBody
    // );
    throw new Error(
      `Verification failed with status ${response.status}`
    );
  }

  const data = await response.json();
  // console.log('[API/Jobs] Job verified successfully:', data);

  return data;
}
